import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { GiftCardContract } from '../../../types/GiftCard';
import { CartPaymentProfileContract } from '../../../types/PaymentProfile';
import { UIConstants } from '../../../utils/constants';
import AmazonPayLogo from '../../content/AmazonPayLogo';
import SavedCardSummary from '../SavedCardSummary';
import SectionSummary, { SectionSummaryGroupPosition } from '../SectionSummary';
import GiftCardPaymentSummary from '../../content/GiftCardPaymentSummary';
import PayPalLogo from '../../content/PayPalLogo';
import { useMst } from '../../../models/stores/Root';
import StripeLogo from '../../content/StripeLogo';

export type PaymentSummaryProps = {
  paymentProfile: CartPaymentProfileContract | null;
  groupPosition?: SectionSummaryGroupPosition;
  onPaymentEdit: () => void;
  appliedGiftCards: GiftCardContract[];
};

const PaymentSummary: React.FC<PaymentSummaryProps> = observer(
  ({
    paymentProfile,
    groupPosition,
    onPaymentEdit,
    appliedGiftCards,
  }: PaymentSummaryProps) => {
    const rootStore = useMst();
    const { cart } = rootStore;

    const renderContent = useCallback((): React.ReactNode => {
      let paymentMethodSummaryContent = (
        <div className="text-sm font-medium">
          {UIConstants.NO_SAVED_PAYMENT_CARDS}
        </div>
      );

      if (paymentProfile && paymentProfile.method) {
        switch (paymentProfile.method?.toLowerCase()) {
          case 'volusionpayments':
            paymentMethodSummaryContent = !paymentProfile.card?.last4 ? (
              <StripeLogo
                stripeMethodType={cart.stripeInfo.cardBrand ?? undefined}
              />
            ) : (
              <SavedCardSummary card={paymentProfile.card} />
            );
            break;
          case 'credit card':
            paymentMethodSummaryContent = (
              <SavedCardSummary card={paymentProfile.card} />
            );
            break;
          case 'paypal express upgrade':
            paymentMethodSummaryContent = !paymentProfile.card?.last4 ? (
              <PayPalLogo />
            ) : (
              <SavedCardSummary card={paymentProfile.card} />
            );
            break;
          case 'purchase order number':
            paymentMethodSummaryContent = (
              <div className="flex flex-row text-sm">
                <span className="font-medium">{paymentProfile.method}</span>
                <span className="ml-2">{paymentProfile.poNumber}</span>
              </div>
            );
            break;
          case 'amazon pay':
            paymentMethodSummaryContent = <AmazonPayLogo />;
            break;
          default:
            paymentMethodSummaryContent = (
              <div className="flex flex-row text-sm">
                <div className="text-sm font-medium">
                  {paymentProfile.method}
                </div>
              </div>
            );
            break;
        }
      }
      if (appliedGiftCards.length > 0) {
        paymentMethodSummaryContent = (
          <>
            {paymentMethodSummaryContent}
            <GiftCardPaymentSummary
              giftCards={appliedGiftCards}
              hasPaymentProfileMethod={Boolean(
                paymentProfile && paymentProfile.method
              )}
            />
          </>
        );
      }
      return paymentMethodSummaryContent;
    }, [appliedGiftCards, paymentProfile, cart.stripeInfo.cardBrand]);

    return (
      <SectionSummary
        title="Payment"
        content={renderContent()}
        displayActionButton
        actionButtonText={
          paymentProfile && paymentProfile.method ? 'Edit' : 'Choose'
        }
        onActionButtonClicked={onPaymentEdit}
        groupPosition={groupPosition}
        toolTipKey="PaymentSummaryKey"
        linkButtonDataTestId="LinkButton-input-Payment-Summary"
      />
    );
  }
);

export default PaymentSummary;
